import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter, type Router } from 'vue-router'
import { useStore, type Store } from 'vuex'
import { useAdmin } from '@/composables/useAdmin'
import { useAdminDB } from '@/composables/useAdminDB'
import { updateAvailability } from '@/composables/useAvailability'
import { useFloatChats } from '@/composables/useFloatChats'
import { useAgentChat } from '@/composables/useAgentChat'
import { showAutoLogoutWarningNotification } from '@/services/notifications'
import type { AuthUser } from '@/types/auth'
import { resetTeamsWithMembers } from '@/composables/useTeamsWithMembers'
import { resetConversationStore } from '@/composables/useConversationStore'
import { resetMessageCache } from '@/composables/useMessageCache'
import { stopDashboardEngine, scopeReady } from '@/composables/useDashboardEngine'
import { resetAssignmentAlerts } from '@/composables/useAssignmentAlert'
import { resetForceLogout } from '@/composables/useForceLogout'
import { resetHeaderState } from '@/composables/useHeaderState'
import { closeCurrentSession } from '@/composables/useSessionTracking'

// updateAvailability()/closeCurrentSession() usam fetch() sem timeout próprio
// — contra o Chatwoot self-hosted (que já travou antes sob carga, ver storm
// de team_members) uma request pode ficar pendurada sem nunca resolver NEM
// rejeitar. performLogout() aguardava essas chamadas antes de limpar a sessão
// e navegar pro login — uma request assim deixava o logout "preso" pra
// sempre, sem erro nenhum no console. Timeout aqui garante que o logout local
// (limpar sessão + navegar) sempre acontece, best-effort pro resto.
export const LOGOUT_NETWORK_TIMEOUT_MS = 4_000
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), ms)),
  ])
}

const WARNING_SECONDS = 30
const ACTIVITY_THROTTLE_MS = 5_000
const CHECK_INTERVAL_MS = 1_000
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const
// lastActivityTs é estado de módulo por ABA (cada aba tem seu próprio realm JS) —
// sem isso, atividade numa aba não impede o aviso de aparecer em outra.
const ACTIVITY_STORAGE_KEY = 'wootdash_last_activity'

// ── Estado de módulo (singleton) — exportado diretamente para o modal poder
// consumir sem re-chamar useAutoLogout() (que arma os efeitos colaterais:
// listeners de atividade, setInterval, watch de rota). ──
export const showWarning  = ref(false)
export const secondsLeft  = ref(WARNING_SECONDS)
// Segundos até o aviso disparar sozinho (null = desativado nesta rota/config)
// — alimenta o botão de teste no header, só leitura, não interfere no fluxo real.
export const secondsUntilWarning = ref<number | null>(null)
let lastActivityTs = Date.now()
let checkTimer: ReturnType<typeof setInterval> | null = null
let listenersBound = false
// Enquanto true, bumpActivity/handleStorageActivity ignoram eventos de atividade —
// protege o rebobinamento de lastActivityTs feito por triggerTestWarning() do
// próprio clique (e de um mousemove incidental) até o tick() seguinte assumir
// via showWarning.value = true.
let forcingTestWarning = false

function bumpActivity() {
  // Enquanto o aviso está visível, atividade incidental (ex.: mexer o mouse pra
  // ler o popup) NÃO deve resetar a contagem nem escondê-lo — só um clique
  // explícito em "Continuar logado" (continueSession) faz isso.
  if (showWarning.value || forcingTestWarning) return
  const now = Date.now()
  if (now - lastActivityTs < ACTIVITY_THROTTLE_MS) return
  lastActivityTs = now
  broadcastActivity(now)
}

/** Grava o timestamp no localStorage pra outras abas (evento 'storage' não dispara na própria aba que escreveu). */
function broadcastActivity(ts: number) {
  try { localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts)) } catch { /* ignore (private mode etc.) */ }
}

// Ao contrário de bumpActivity, aqui o sinal já vem de fora (outra aba) — não
// tem o "mexer o mouse pra ler o popup" pra proteger, então sempre reseta e
// esconde o aviso, mesmo se showWarning já estiver true nesta aba.
function handleStorageActivity(e: StorageEvent) {
  if (forcingTestWarning) return
  if (e.key !== ACTIVITY_STORAGE_KEY || !e.newValue) return
  const ts = Number(e.newValue)
  if (!Number.isFinite(ts) || ts <= lastActivityTs) return
  lastActivityTs = ts
  showWarning.value = false
}

// ── Alerta visual da aba (título/favicon/badge) enquanto o aviso está ativo —
// reforça o toast/modal pra quem só olha de canto de olho a barra de abas. ──
const TAB_ALERT_ICON_SIZE = 64
let originalTitle: string | null = null
let originalFaviconHref: string | null = null
let alertFaviconDataUrl: string | null = null

function getFaviconLink(): HTMLLinkElement | null {
  return document.querySelector('link[rel="icon"]')
}

/** Desenha o favicon de alerta uma única vez (canvas) — evita depender de um asset extra no repo. */
function buildAlertFaviconDataUrl(): string | null {
  if (alertFaviconDataUrl) return alertFaviconDataUrl
  try {
    const canvas = document.createElement('canvas')
    canvas.width = TAB_ALERT_ICON_SIZE
    canvas.height = TAB_ALERT_ICON_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    const r = TAB_ALERT_ICON_SIZE / 2
    ctx.beginPath()
    ctx.arc(r, r, r, 0, Math.PI * 2)
    ctx.fillStyle = '#e53935'
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = `bold ${Math.round(TAB_ALERT_ICON_SIZE * 0.6)}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('!', r, r + TAB_ALERT_ICON_SIZE * 0.04)
    alertFaviconDataUrl = canvas.toDataURL('image/png')
    return alertFaviconDataUrl
  } catch {
    return null
  }
}

function tabAlertTitle(secs: number): string {
  return `⚠️ ${secs}s — Ainda aí?`
}

function startTabAlert(secs: number) {
  if (originalTitle === null) originalTitle = document.title
  document.title = tabAlertTitle(secs)

  const link = getFaviconLink()
  if (link) {
    if (originalFaviconHref === null) originalFaviconHref = link.href
    const dataUrl = buildAlertFaviconDataUrl()
    if (dataUrl) link.href = dataUrl
  }

  navigator.setAppBadge?.().catch(() => { /* API opcional — sem suporte não é erro */ })
}

function updateTabAlert(secs: number) {
  if (originalTitle === null) return // alerta não está ativo nesta aba
  document.title = tabAlertTitle(secs)
}

function stopTabAlert() {
  if (originalTitle !== null) {
    document.title = originalTitle
    originalTitle = null
  }
  const link = getFaviconLink()
  if (link && originalFaviconHref !== null) {
    link.href = originalFaviconHref
    originalFaviconHref = null
  }
  navigator.clearAppBadge?.().catch(() => { /* ignore */ })
}

function bindActivityListeners() {
  if (listenersBound) return
  listenersBound = true
  ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, bumpActivity, { passive: true }))
  window.addEventListener('storage', handleStorageActivity)
}

function unbindActivityListeners() {
  if (!listenersBound) return
  listenersBound = false
  ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, bumpActivity))
  window.removeEventListener('storage', handleStorageActivity)
}

/**
 * Centraliza o logout (dispatch + redirect) — reusado por App.vue, MobileView.vue
 * e pelo botão "Deslogar agora" do modal de inatividade, evitando reimplementar
 * store.dispatch('logout') + router.push em cada lugar.
 *
 * Antes de limpar a sessão, marca o usuário como offline (Chatwoot + agent_status)
 * — precisa ler o token/id do store ANTES do dispatch('logout'), que já limpa
 * o usuário autenticado. Também fecha as janelas flutuantes de conversa
 * (useFloatChats — estado de módulo, sobrevive à troca de rota): sem isso
 * elas continuavam abertas na tela de login. Mesmo motivo pro chat entre
 * agentes (useAgentChat.reset — para o polling e limpa o estado).
 */
export async function performLogout(store: Store<any>, router: Router) {
  showWarning.value = false
  unbindActivityListeners()
  if (checkTimer) { clearInterval(checkTimer); checkTimer = null }

  const user = store.getters.authUser as AuthUser | null
  if (user?.access_token) {
    try {
      await withTimeout(updateAvailability(user.access_token, 'offline', user.id), LOGOUT_NETWORK_TIMEOUT_MS)
    } catch (err) {
      console.warn('[AutoLogout] updateAvailability(offline):', err)
    }
  }

  useFloatChats().closeAll()
  useAgentChat().reset()
  await withTimeout(closeCurrentSession(), LOGOUT_NETWORK_TIMEOUT_MS)
  // Fase 4.2 do plano de otimização de login (docs/planos/PLANO-OTIMIZACAO-LOGIN.md):
  // zera todo módulo de estado singleton ligado à sessão do agente que está
  // saindo — hoje o reload pós-login (LoginView.vue) mascara a ausência disso,
  // mas essa máscara só existe enquanto o reload continuar existindo (ver
  // Fase 4.1/4.3/4.4). stopChatwootRealtime()/broadcast.stopPolling() não
  // entram aqui — já são acionados reativamente pelo watch(authUser) em
  // App.vue assim que o dispatch('logout') abaixo zera o usuário.
  resetTeamsWithMembers()
  resetConversationStore()
  resetMessageCache()
  stopDashboardEngine()
  resetAssignmentAlerts()
  resetForceLogout()
  resetHeaderState()
  await store.dispatch('logout')
  router.push({ name: 'login' })
}

/**
 * Reseta a contagem de inatividade e fecha o aviso — botão "Continuar logado".
 * Também atualiza o último acesso do usuário (mesmo registro da tela Admin →
 * Últimos Acessos), já que o agente confirmou que continua ativo na dashboard.
 */
export function continueSession(store: Store<any>) {
  lastActivityTs = Date.now()
  broadcastActivity(lastActivityTs)
  showWarning.value = false
  secondsLeft.value = WARNING_SECONDS

  const user = store.getters.authUser as AuthUser | null
  if (user) {
    useAdminDB().logAccess(user.id, user.email, user.name)
      .catch(err => console.warn('[AutoLogout] logAccess:', err))
  }
}

/**
 * Botão de teste no header (Ctrl+Shift+*) — força o aviso real de inatividade
 * (toast + título da aba + modal) sem esperar o tempo configurado. Rebobina
 * lastActivityTs pro instante exato em que o aviso ligaria, deixando o tick()
 * seguinte assumir dali — é o mesmo caminho de produção, não uma simulação à parte.
 * Se autoLogoutMinutes estiver 0 (recurso desativado), usa uma janela mínima só
 * pra permitir o teste mesmo sem configurar nada em Admin → Configurações Gerais.
 */
export function triggerTestWarning() {
  const { globalSettings } = useAdmin()
  const minutes  = globalSettings.value.autoLogoutMinutes
  const limitMs  = minutes > 0 ? minutes * 60_000 : (WARNING_SECONDS + 5) * 1000
  const warnAtMs = Math.max(0, limitMs - WARNING_SECONDS * 1000)

  forcingTestWarning = true
  // -100ms de folga pra garantir elapsed >= warnAtMs já no próximo tick (1s).
  lastActivityTs = Date.now() - warnAtMs - 100
}

/**
 * Arma o timer de inatividade + listeners de atividade. Chamado uma única vez,
 * no setup() de App.vue (único componente raiz, nunca desmonta durante a
 * navegação da SPA) — não chamar de novo em outros componentes, isso criaria
 * um segundo setInterval/par de listeners redundante.
 */
export function useAutoLogout() {
  const { globalSettings, updateGlobalSettings } = useAdmin()
  const db     = useAdminDB()
  const route  = useRoute()
  const store  = useStore()
  const router = useRouter()

  const isExempt = computed(() => route.name === 'login' || route.name === 'sla-metro')

  function tick() {
    const minutes = globalSettings.value.autoLogoutMinutes
    if (!minutes || minutes <= 0 || isExempt.value) {
      if (showWarning.value) showWarning.value = false
      secondsUntilWarning.value = null
      return
    }

    const limitMs  = minutes * 60_000
    const warnAtMs = limitMs - WARNING_SECONDS * 1000
    const elapsed  = Date.now() - lastActivityTs

    secondsUntilWarning.value = Math.max(0, Math.ceil((warnAtMs - elapsed) / 1000))

    if (elapsed >= limitMs) {
      performLogout(store, router)
      return
    }
    if (elapsed >= warnAtMs) {
      const remainingSecs = Math.max(0, Math.ceil((limitMs - elapsed) / 1000))
      // Dispara o toast só na transição pra "avisando" — não a cada tick (1s).
      if (!showWarning.value) {
        showAutoLogoutWarningNotification(remainingSecs).catch(() => { /* melhor esforço — modal em tela já cobre o aviso */ })
      }
      showWarning.value = true
      secondsLeft.value = remainingSecs
    } else {
      showWarning.value = false
    }
  }

  // Único ponto que aciona o alerta visual da aba — cobre todos os caminhos que
  // mudam showWarning/secondsLeft (tick, continueSession, reset entre abas,
  // saída de rota isenta) sem precisar chamar startTabAlert/stopTabAlert em cada um.
  watch(showWarning, (active) => {
    if (active) {
      startTabAlert(secondsLeft.value)
      forcingTestWarning = false // tick() já assumiu — activity listeners podem voltar a valer
    } else {
      stopTabAlert()
    }
  })
  watch(secondsLeft, (secs) => {
    if (showWarning.value) updateTabAlert(secs)
  })

  // Arma/desarma os listeners de atividade conforme a rota (login/SLA Telão ficam isentos).
  watch(isExempt, (exempt) => {
    if (exempt) {
      unbindActivityListeners()
      showWarning.value = false
    } else {
      lastActivityTs = Date.now() // evita logout imediato ao sair de uma rota isenta
      bindActivityListeners()
    }
  }, { immediate: true })

  onMounted(() => {
    // A tela Admin é o único lugar que hoje chama db.loadSettings() — um agente
    // comum nunca a abre, então sem isso o valor configurado no banco nunca
    // chegaria até ele. Sincroniza aqui todo setting global lido por telas de
    // agente comum (não só auto-logout) pelo mesmo motivo.
    const syncSettings = () => {
      db.loadSettings().then(settings => {
        if ('auto_logout_minutes' in settings) {
          const n = Number(settings.auto_logout_minutes)
          updateGlobalSettings({ autoLogoutMinutes: Number.isFinite(n) && n >= 0 ? n : 0 })
        }
        if ('restricted_template_sending_enabled' in settings) {
          updateGlobalSettings({ restrictedTemplateSendingEnabled: settings.restricted_template_sending_enabled === 'true' })
        }
      }).catch(() => { /* mantém localStorage/default */ })
    }
    // Fase 3 do plano de carregamento pós-login (PLANO-OTIMIZACAO-CARREGAMENTO-DASHBOARD.md):
    // adiada pra depois de scopeReady — não é urgente (não afeta o primeiro
    // render de nada), então tirá-la da rajada inicial que concorre com
    // get_conversations no mesmo webhook n8n só ganha, sem mudar comportamento.
    if (scopeReady.value) {
      syncSettings()
    } else {
      const stopWatchingScope = watch(scopeReady, ready => {
        if (!ready) return
        stopWatchingScope()
        syncSettings()
      })
    }

    if (!checkTimer) checkTimer = setInterval(tick, CHECK_INTERVAL_MS)
  })

  onUnmounted(() => {
    if (checkTimer) { clearInterval(checkTimer); checkTimer = null }
    unbindActivityListeners()
    stopTabAlert()
  })
}
